import { useEffect, useState } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router.js';
import { ApiClientError, bootstrapSession, onAuthChange, api, logout } from '../lib/api/client.js';
import { useSession } from '../lib/auth/session.js';
import type { SessionBootstrap } from '@arcnet/contracts';
import { LoginPage } from '../features/auth/LoginPage.js';
import { AcceptInvitePage } from '../features/auth/AcceptInvitePage.js';
import { ForgotPasswordPage, ResetPasswordPage } from '../features/auth/PasswordResetPages.js';
import { AuthLayout } from '../features/auth/AuthLayout.js';
import { MfaRequiredPage } from '../features/auth/MfaRequiredPage.js';
import { Toaster, toastError } from '../components/ui/Toast.js';
import { ConfirmHost } from '../components/ui/Confirm.js';
import { StepUpHost } from '../lib/auth/stepup.js';

/**
 * The API saying 2FA must be set up first (a session from before it became compulsory, 3 October
 * 2026): the session is reloaded, and the setup screen takes over.
 */
const MFA_SETUP = 'auth.mfa_setup_required';
let reloading = false;
function onMfaSetupRequired(error: unknown): boolean {
  if (!(error instanceof ApiClientError) || error.code !== MFA_SETUP) return false;
  if (!reloading) {
    reloading = true;
    void api.get<SessionBootstrap>('/v1/auth/me')
      .then((me) => useSession.getState().setSession(me))
      .finally(() => { reloading = false; });
  }
  return true;
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (error) => { onMfaSetupRequired(error); } }),
  /**
   * A failed save or action is a toast, wherever it happened (components/ui/Toast.tsx,
   * owner, 15 September 2026). A form that says its own failure in place sets
   * `meta: { toast: false }` on its mutation. A 401 is the session ending, which the
   * sign-in screen says by appearing.
   */
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (onMfaSetupRequired(error)) return;
      if (mutation.meta?.['toast'] === false) return;
      if ((error as { status?: number }).status === 401) return;
      // Not a failure: the step-up prompt asks for the password and retries (lib/auth/stepup.tsx),
      // and declining it is the person's own choice.
      if (error instanceof ApiClientError
        && (error.code === 'auth.step_up_required' || error.code === 'auth.step_up_cancelled')) return;
      toastError(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) => {
        // Never retry an auth or authorisation failure: the client already
        // attempted a single-flight refresh, so retrying just burns requests
        // and delays showing the login screen.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return count < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const setSession = useSession((s) => s.setSession);
  const clear = useSession((s) => s.clear);
  const mfaOwed = useSession((s) => !!s.session && s.session.user.mfa_enforced && !s.session.user.mfa_enabled);

  useEffect(() => onAuthChange((v) => { if (!v) { clear(); setAuthed(false); } }), [clear]);

  useEffect(() => {
    // An existing HttpOnly refresh cookie silently restores the session, so a
    // page reload does not send the user back to the login screen.
    void (async () => {
      const ok = await bootstrapSession();
      if (ok) {
        try {
          const me = await api.get<SessionBootstrap>('/v1/auth/me');
          setSession(me);
          setAuthed(true);
        } catch { setAuthed(false); }
      }
      setReady(true);
    })();
  }, [setSession]);

  useEffect(() => {
    if (!ready) return;
    const el = document.getElementById('arc-splash');
    if (!el) return;
    el.classList.add('arc-splash-out');
    const t = setTimeout(() => el.remove(), 260);
    return () => clearTimeout(t);
  }, [ready]);

  const onSignedIn = async (): Promise<void> => {
    const me = await api.get<SessionBootstrap>('/v1/auth/me');
    setSession(me);
    setAuthed(true);
  };

  // The loading screen is index.html's own (owner, 26 September 2026), up before this code has
  // downloaded; it stays until the session is known and then fades.
  if (!ready) return null;

  // Invitation acceptance is checked BEFORE the auth gate: the person following
  // the link has no account yet, so there is nothing to authenticate. Read
  // straight from the URL rather than through the router, which only mounts
  // once a session exists.
  const inviteToken = /^\/invite\/([\w.-]+)$/.exec(window.location.pathname)?.[1];
  /**
   * An invitation opened in a browser that is already signed in (owner, 18 September 2026: the link
   * went straight into the admin console, and the invited person was never asked for a password).
   * It is for somebody new, so it asks to sign out first; staying signed in goes to the portal.
   */
  if (inviteToken && authed) {
    return (
      <AuthLayout>
            <h1 className="arc-h1">Accept an invitation</h1>
            <p className="arc-muted" style={{ marginTop: 6, fontSize: 13.5, lineHeight: 1.55 }}>
              You are signed in as <strong>{useSession.getState().session?.user.email}</strong>. An invitation
              makes a new sign-in for the person it was sent to, so sign out first to accept it.
            </p>
            <div className="arc-stack" style={{ gap: 8, marginTop: 18 }}>
              <button type="button" className="arc-btn arc-btn-primary" style={{ width: '100%' }}
                onClick={() => { void logout(); }}>
                Sign out and accept
              </button>
              <button type="button" className="arc-btn" style={{ width: '100%' }}
                onClick={() => { window.location.assign('/'); }}>
                Stay signed in
              </button>
            </div>
      </AuthLayout>
    );
  }
  // Forgot password (0176): open whether or not somebody is signed in on this browser.
  const resetPath = window.location.pathname;
  if (resetPath === '/forgot-password' || resetPath === '/reset-password') {
    return resetPath === '/forgot-password' ? <ForgotPasswordPage /> : <ResetPasswordPage />;
  }
  if (inviteToken && !authed) {
    return (
      <QueryClientProvider client={queryClient}>
        <AcceptInvitePage token={inviteToken} onAccepted={onSignedIn} />
        <Toaster />
        <ConfirmHost />
      </QueryClientProvider>
    );
  }

  if (!authed) {
    return (
      <QueryClientProvider client={queryClient}>
        <LoginPage onSignedIn={onSignedIn} />
        <Toaster />
        <ConfirmHost />
      </QueryClientProvider>
    );
  }

  // An administrator without 2FA sets it up before anything else (owner, 3 October 2026).
  if (mfaOwed) {
    return (
      <QueryClientProvider client={queryClient}>
        <MfaRequiredPage />
        <Toaster />
        <ConfirmHost />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
        <ConfirmHost />
        <StepUpHost />
    </QueryClientProvider>
  );
}

